class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;

    const sections = parsedState && parsedState.sections;
    let drawerSectionHtml = sections && sections['cart-drawer'];
    if (!drawerSectionHtml && sections) {
      const dynamicKey = Object.keys(sections).find(
        (k) => k === 'cart-drawer' || k.endsWith('__cart-drawer') || (k.includes('cart-drawer') && !k.includes('icon'))
      );
      if (dynamicKey) drawerSectionHtml = sections[dynamicKey];
    }

    const applyFromSectionHtml = (sectionHtml) => {
      if (!sectionHtml) return false;
      const innerFrag = this.getSectionInnerHTML(sectionHtml, '#CartDrawer');
      if (innerFrag) {
        const mount = this.querySelector('#CartDrawer');
        if (mount) mount.innerHTML = innerFrag;
        return true;
      }
      const sourceInnerNode = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('#CartDrawer .drawer__inner');
      const liveInner = this.querySelector('#CartDrawer .drawer__inner');
      if (sourceInnerNode && liveInner) {
        liveInner.replaceWith(sourceInnerNode);
        return true;
      }
      return false;
    };

    if (drawerSectionHtml && applyFromSectionHtml(drawerSectionHtml)) {
      this.classList.toggle('is-empty', !this.querySelector('#CartDrawer .cart-item'));
    } else if (typeof routes !== 'undefined' && routes.cart_url) {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((text) => {
          if (applyFromSectionHtml(text)) {
            this.classList.toggle('is-empty', !this.querySelector('#CartDrawer .cart-item'));
          }
        })
        .catch(() => {});
    }

    if (sections) {
      let bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml == null) {
        const bubbleKey = Object.keys(sections).find((k) => k.includes('cart-icon-bubble'));
        if (bubbleKey) bubbleHtml = sections[bubbleKey];
      }
      if (bubbleHtml) {
        const bubbleLink = document.getElementById('cart-icon-bubble');
        if (bubbleLink) {
          const inner = this.getSectionInnerHTML(bubbleHtml, '.shopify-section');
          if (inner !== '') bubbleLink.innerHTML = inner;
        }
      }
    }

    setTimeout(() => {
      const ov = this.querySelector('#CartDrawer-Overlay');
      if (ov) ov.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (html == null) {
      return '';
    }
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Keep the rendered drawer honest about what is actually in the cart.
 *
 * The drawer is server-rendered once and then sits in the DOM. Nothing tells it
 * when the cart changes somewhere else — most visibly when a customer taps
 * through to checkout and comes back: iOS Safari restores the whole page from
 * bfcache, so the drawer redraws exactly as it was, while checkout has since
 * added or dropped the checkout app's own hidden line. Line item keys mean a
 * stale drawer can no longer act on the wrong product, but it still shows the
 * wrong prices and totals, and its first click still fails. So compare the
 * cart it rendered against the live one whenever the page comes back into use,
 * and re-render when they disagree.
 */
(function () {
  const RECHECK_INTERVAL = 1000;
  let inFlight = false;
  let lastCheck = 0;

  function drawerItems() {
    return document.querySelector('cart-drawer-items');
  }

  /* Count as well as keys: a pure quantity change moves neither key nor line
     order, and the drawer's prices, tier buttons and totals all key off it.

     Totals too, because the drawer renders money the line list alone does not
     account for. A discount that changes what is charged while leaving the
     lines untouched moves neither the count nor the keys, so without these the
     drawer would keep showing the old per-line savings and checkout amount.

     Must stay byte-identical to data-cart-signature in
     snippets/cart-drawer.liquid; both fields are integer cents on both sides. */
  function liveSignature(cart) {
    return `${cart.item_count}|${cart.total_price}|${cart.total_discount}|${cart.items
      .map((item) => item.key)
      .join(',')}`;
  }

  /* Shipping protection is sold once per order, but the app re-adds its line
     without noticing the one already there — most reliably when the customer
     taps Back out of checkout onto a bfcache-restored page and hits the
     checkout button a second time. The result is a doubled protection charge
     the customer never chose. The app owns that line, so the theme cannot stop
     the add; it can hold the line at a single unit whenever it looks at the
     cart. Handles come from the same setting the drawer already uses to keep
     these lines out of the box count and the subtotal.

     Returns true when it changed anything, so the caller re-renders. */
  function clampAppLines(items, cart) {
    const configured = (items.dataset.cartAppHandles || '')
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);
    if (!configured.length) return Promise.resolve(false);

    /* `handle` is not guaranteed on a cart line across API versions; the
       product URL always carries it. */
    const handleOf = (item) => {
      if (item.handle) return String(item.handle).toLowerCase();
      const match = /\/products\/([^/?#]+)/.exec(item.url || '');
      return match ? match[1].toLowerCase() : '';
    };

    /* The first protection line is held at one unit and any duplicate of it is
       dropped. Only the configured handles are ever touched.

       One change per pass, re-read from the cart that change returned: every
       write rehashes the keys of the lines it did not touch, so a second key
       picked out of the cart we started from would already be dead. The guard
       is what makes that recursion safe if the app keeps re-adding. */
    const step = (currentCart, changed, guard) => {
      if (guard <= 0) return Promise.resolve(changed);

      let target = null;
      currentCart.items
        .filter((item) => configured.includes(handleOf(item)))
        .forEach((item, index) => {
          if (target) return;
          const wanted = index === 0 ? 1 : 0;
          if (item.quantity !== wanted) target = { id: item.key, quantity: wanted };
        });
      if (!target) return Promise.resolve(changed);

      return fetch(`${routes.cart_change_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(target),
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((next) => (next && next.items ? step(next, true, guard - 1) : changed))
        .catch(() => changed);
    };

    return step(cart, false, 4);
  }

  function refreshCartIconBubble() {
    const bubbleLink = document.getElementById('cart-icon-bubble');
    if (!bubbleLink) return;
    fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((text) => {
        const node = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
        if (node && node.innerHTML !== '') bubbleLink.innerHTML = node.innerHTML;
      })
      .catch(() => {});
  }

  function resyncIfStale() {
    if (typeof routes === 'undefined' || !routes.cart_url) return;

    const items = drawerItems();
    /* No stamp means an older render of the drawer markup — there is nothing to
       compare against, and refreshing on every wake would be worse than not. */
    if (!items || items.dataset.cartSignature === undefined) return;

    const now = Date.now();
    if (inFlight || now - lastCheck < RECHECK_INTERVAL) return;
    inFlight = true;
    lastCheck = now;

    fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) =>
        clampAppLines(items, cart).then((clamped) => {
          if (!clamped && liveSignature(cart) === items.dataset.cartSignature) return;
          refreshCartIconBubble();
          if (typeof items.onCartUpdate === 'function') return items.onCartUpdate();
        })
      )
      .catch(() => {})
      .finally(() => {
        inFlight = false;
      });
  }

  /* `persisted` is the bfcache restore — the Back-out-of-checkout case. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resyncIfStale();
  });

  /* Browsers that skipped bfcache, and returning to a backgrounded tab. */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resyncIfStale();
  });
})();
